import { useLayoutEffect, useRef, useState } from "react";
import { Trans } from "@lingui/react/macro";
import Image from "next/image";
import { cn } from "@marketing-sites/utils/cn";

import { DemoInViewContext } from "./hooks";
import type { DemoAttachment } from "./tickets";

export const THREAD_SCROLL = "overflow-y-auto [scrollbar-width:thin]";
export const PRODUCT_FONT =
  "font-[system-ui,-apple-system,'Segoe_UI',Roboto,sans-serif]";

export function Avatar({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-full bg-[#b9a6f5] text-[10px] font-semibold text-[#1a1a1a]",
        className,
      )}
    >
      {[...name][0]}
    </span>
  );
}

export function Kicker({
  product,
  className,
}: {
  product: "helpdesk" | "fin";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "border-chalk mb-3 flex w-fit items-center gap-x-1.5 border bg-white px-3 py-[7px] font-mono text-[11px] uppercase text-[#111]",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "block size-2",
          product === "helpdesk" ? "bg-helpdesk" : "bg-fin",
        )}
      />
      {product === "helpdesk" ? (
        <Trans>Intercom helpdesk</Trans>
      ) : (
        <Trans>Fin AI Agent</Trans>
      )}
    </span>
  );
}

export function Scaled({
  width,
  children,
}: {
  width: number;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setScale(el.getBoundingClientRect().width / width);
    const observer = new ResizeObserver(([entry]) =>
      setScale(entry.contentRect.width / width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);
  return (
    <div ref={ref} className="absolute inset-0">
      <div
        className={cn("origin-top-left", PRODUCT_FONT)}
        style={{
          width,
          height: scale ? `${100 / scale}%` : undefined,
          transform: `scale(${scale})`,
        }}
      >
        <DemoInViewContext value>{children}</DemoInViewContext>
      </div>
    </div>
  );
}

export function NavIcon({ d, fill }: { d: string; fill?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4"
      fill={fill ? "currentColor" : "none"}
      stroke={fill ? "none" : "currentColor"}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

export function Attachment({ attachment }: { attachment: DemoAttachment }) {
  if (attachment.type === "pdf") {
    return (
      <span className="flex w-[200px] items-center gap-2.5 rounded-xl border border-black/[0.08] bg-white p-2.5">
        <span
          aria-hidden
          className="flex h-9 w-7 shrink-0 items-end justify-center rounded-[3px] bg-[#e5484d] pb-1 text-[7px] font-bold text-white"
        >
          PDF
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[12px] font-semibold text-[#111]">
            {attachment.name}
          </span>
          <span className="text-[11px] text-black/50">{attachment.size}</span>
        </span>
      </span>
    );
  }
  return (
    <Image
      src={attachment.src}
      alt={attachment.name}
      sizes="160px"
      className="h-[120px] w-[160px] rounded-xl border border-black/[0.08] object-cover"
    />
  );
}
